import { FC, useState } from 'react';
import { Box, Modal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import styles from './TravelBuddies.module.scss';
import { useGetTripTravelBuddiesQuery, useUpdateTripMutation } from '../../../../../store/services/tripsApi';
import { useIsMobile } from '../../../../../hooks/useWindowWidth';
import CustomButton from '../../../../../common/CustomButton';
import AddTravelBuddiesModal from '../../../MyTrips/components/AddTravelBuddiesModal';
import { TravelBuddy } from '../../../../../types/trip.types';
import TravelBuddyCard from './TravelBuddyCard/TravelBuddyCard';

interface TravelBuddiesProps {
  tripId: number;
}

const TravelBuddies: FC<TravelBuddiesProps> = ({ tripId }) => {
  const isMobile = useIsMobile();
  const [showAddBuddiesModal, setShowAddBuddiesModal] = useState(false);
  const [selectedBuddies, setSelectedBuddies] = useState<TravelBuddy[]>([]);
  const [buddyActionError, setBuddyActionError] = useState<string | null>(null);
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);
  const [buddyIdPendingRemove, setBuddyIdPendingRemove] = useState<number | null>(null);
  const [isRemovingBuddy, setIsRemovingBuddy] = useState(false);

  const [updateTrip] = useUpdateTripMutation();
  const { data: travelBuddiesData, isLoading, error, refetch } = useGetTripTravelBuddiesQuery(tripId.toString());

  const handleAddBuddiesOpen = () => {
    setBuddyActionError(null);
    setShowAddBuddiesModal(true);
  };

  const handleAddBuddiesClose = () => {
    setShowAddBuddiesModal(false);
    setSelectedBuddies([]);
  };

  const handleBuddiesChange = (buddies: TravelBuddy[]) => {
    setSelectedBuddies(buddies);
  };

  const handleConfirmAddBuddies = async (buddies: TravelBuddy[]) => {
    if (!travelBuddiesData) {
      setBuddyActionError('Trip data is not ready. Try again.');
      throw new Error('No travel buddies data');
    }
    setBuddyActionError(null);
    const existingIds = travelBuddiesData.buddies.map(b => b.id);
    const newIds = buddies.map(b => b.id);
    const mergedBuddyIds = Array.from(new Set([...existingIds, ...newIds]));
    try {
      await updateTrip({ id: tripId, buddies: mergedBuddyIds }).unwrap();
      await refetch();
    } catch (e: unknown) {
      const message =
        typeof e === 'object' && e !== null && 'data' in e
          ? String((e as { data?: { message?: string } }).data?.message ?? '')
          : '';
      setBuddyActionError(message || 'Failed to add travel buddies');
      throw e;
    }
  };

  const openRemoveBuddyConfirm = (buddyId: number) => {
    setBuddyActionError(null);
    setBuddyIdPendingRemove(buddyId);
    setRemoveConfirmOpen(true);
  };

  const closeRemoveBuddyConfirm = () => {
    if (isRemovingBuddy) return;
    setRemoveConfirmOpen(false);
    setBuddyIdPendingRemove(null);
  };

  const handleConfirmRemoveBuddy = async () => {
    if (!travelBuddiesData || buddyIdPendingRemove == null) return;

    const buddyId = buddyIdPendingRemove;
    setBuddyActionError(null);
    setIsRemovingBuddy(true);

    const remainingIds = travelBuddiesData.buddies.filter(b => b.id !== buddyId).map(b => b.id);

    try {
      await updateTrip({ id: tripId, buddies: remainingIds }).unwrap();
      await refetch();
      setRemoveConfirmOpen(false);
      setBuddyIdPendingRemove(null);
    } catch (e: unknown) {
      const message =
        typeof e === 'object' && e !== null && 'data' in e
          ? String((e as { data?: { message?: string } }).data?.message ?? '')
          : '';
      setBuddyActionError(message || 'Failed to remove travel buddy');
    } finally {
      setIsRemovingBuddy(false);
    }
  };

  if (isLoading) {
    return (
      <Box className={styles.travelBuddies}>
        <Typography variant="h7">Loading travel buddies...</Typography>
      </Box>
    );
  }

  if (error || !travelBuddiesData) {
    return (
      <Box className={styles.travelBuddies}>
        <Typography
          variant="h7"
          color="error"
        >
          Failed to load travel buddies
        </Typography>
      </Box>
    );
  }

  const { trip_creator, buddies } = travelBuddiesData;

  const pendingBuddy = buddyIdPendingRemove != null ? buddies.find(b => b.id === buddyIdPendingRemove) : undefined;
  const pendingBuddyName = pendingBuddy?.full_name?.trim() || pendingBuddy?.email || 'this travel buddy';

  return (
    <>
      <Box className={styles.travelBuddies}>
        <Box className={styles.header}>
          <Typography
            variant={isMobile ? 'h8' : 'h7'}
            className={styles.sectionTitle}
          >
            Travel Buddies
          </Typography>

          <CustomButton
            variant="contained"
            onClick={handleAddBuddiesOpen}
            customClass={styles.addButton}
          >
            {isMobile ? '+ Add' : '+ Add New'}
          </CustomButton>
        </Box>

        {buddyActionError && (
          <Typography
            variant="h10"
            sx={{ color: '#C8102E' }}
          >
            {buddyActionError}
          </Typography>
        )}

        <Box className={styles.buddiesList}>
          {trip_creator && (
            <TravelBuddyCard
              buddy={trip_creator}
              isCreator={true}
            />
          )}
          {buddies.map(buddy => (
            <TravelBuddyCard
              key={buddy.id}
              buddy={buddy}
              isCreator={false}
              onRemove={openRemoveBuddyConfirm}
            />
          ))}
          {buddies.length === 0 && !trip_creator && (
            <Box className={styles.emptyState}>
              <Typography
                variant="h10"
                sx={{ color: '#5E6A81', textAlign: 'center', py: 3 }}
              >
                No travel buddies added yet. Invite your friends to join this trip!
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
      <AddTravelBuddiesModal
        open={showAddBuddiesModal}
        onClose={handleAddBuddiesClose}
        selectedBuddies={selectedBuddies}
        onBuddiesChange={handleBuddiesChange}
        onConfirm={handleConfirmAddBuddies}
      />

      <Modal
        open={removeConfirmOpen}
        onClose={closeRemoveBuddyConfirm}
        aria-labelledby="remove-buddy-modal-title"
        aria-describedby="remove-buddy-modal-description"
        disableEscapeKeyDown={isRemovingBuddy}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(34, 43, 58, 0.2)',
          },
        }}
      >
        <Box className={styles.removeConfirmModal}>
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              right: 20,
              cursor: isRemovingBuddy ? 'default' : 'pointer',
            }}
          >
            <CloseIcon
              sx={{ width: 30, height: 30, opacity: isRemovingBuddy ? 0.5 : 1 }}
              onClick={() => {
                if (!isRemovingBuddy) closeRemoveBuddyConfirm();
              }}
            />
          </Box>

          <Box className={styles.removeConfirmModalContent}>
            <Typography
              id="remove-buddy-modal-title"
              variant="h5"
              sx={{ fontWeight: 'bold', textAlign: 'center', color: '#222B3A' }}
            >
              Remove from trip?
            </Typography>
            <Typography
              id="remove-buddy-modal-description"
              variant="body1"
              sx={{ color: '#222B3A', textAlign: 'center' }}
            >
              Are you sure you want to remove {pendingBuddyName} from this trip? They will no longer be listed as a
              travel buddy on this trip.
            </Typography>

            <Box className={styles.removeConfirmActions}>
              <CustomButton
                variant="outlined"
                onClick={closeRemoveBuddyConfirm}
                disabled={isRemovingBuddy}
              >
                Cancel
              </CustomButton>
              <CustomButton
                variant="contained"
                onClick={handleConfirmRemoveBuddy}
                disabled={isRemovingBuddy}
                customClass={styles.removeConfirmDeleteButton}
              >
                {isRemovingBuddy ? 'Removing...' : 'Remove'}
              </CustomButton>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
};

export default TravelBuddies;
